import { type ReactNode, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import {
  Switch,
  Tooltip,
  Icon,
  Typography,
  Box,
  type ITooltipProps,
  type TextStylesType,
  Checkbox,
} from 'shared/ui'
import { logger } from 'shared/lib'
import { LearnMoreLink } from 'pages/settings/ui/LearnMoreLink/LearnMoreLink'
import styles from './styles.module.scss'

export interface IDoubleOptInSettingLayoutProps {
  title: ReactNode
  titleVariant?: TextStylesType
  titleRightContent?: ReactNode
  description?: string | ((value?: boolean) => string)
  learnMoreLink?: string
  checked?: boolean
  onToggle?: (value: boolean) => void
  disabled?: boolean
  renderContent?: (value?: boolean) => ReactNode
  className?: string
  tooltipProps?: ITooltipProps
  switchTooltipProps?: ITooltipProps
  variant?: 'switcher' | 'checkbox' | 'content'
  rightContent?: ReactNode
  switcherLeftContent?: (value?: boolean) => ReactNode
}

export const DoubleOptInSettingLayout = observer(
  ({
    title,
    titleVariant = 'body-md-medium',
    titleRightContent,
    description,
    learnMoreLink,
    checked,
    onToggle,
    disabled,
    renderContent,
    className,
    tooltipProps,
    switchTooltipProps,
    variant = 'switcher',
    rightContent,
    switcherLeftContent,
  }: IDoubleOptInSettingLayoutProps) => {
    const [loading, setLoading] = useState(false)
    const handleOnChange = async (value: boolean) => {
      try {
        setLoading(true)
        await onToggle?.(value)
      } catch (e) {
        logger.error(e)
      } finally {
        setLoading(false)
      }
    }

    const localDisabled = loading || disabled
    return (
      <div className={classNames(styles.item, className)}>
        <div className={styles.infoWrap}>
          <Box display={'flex'} alignItems={'center'} gap={'6px'}>
            <Typography
              ariaLabel='title'
              variant={titleVariant}
              color='var(--content-primary-primary)'
            >
              {title}
              {titleRightContent && (
                <span style={{ display: 'inline-block', paddingLeft: '6px' }}>
                  {titleRightContent}
                </span>
              )}
            </Typography>
            {tooltipProps && (
              <Tooltip placement={'top'} {...tooltipProps}>
                <Icon icon={'question'} color={'var(--content-neutral-primary)'} fontSize={14} />
              </Tooltip>
            )}
          </Box>
          <div className={styles.description}>
            {typeof description === 'function' ? description(checked) : description}
            {learnMoreLink && <LearnMoreLink href={learnMoreLink}>Learn more</LearnMoreLink>}
            {renderContent?.(checked)}
          </div>
        </div>
        {variant === 'switcher' && (
          <Box display={'flex'} alignItems={'center'}>
            {switcherLeftContent && switcherLeftContent?.(checked)}
            <div className={styles.switcherWrap}>
              <Switch
                value={checked}
                onChange={handleOnChange}
                size='middle'
                disabled={localDisabled}
                tooltipProps={switchTooltipProps}
              />
            </div>
          </Box>
        )}
        {variant === 'checkbox' && (
          <Checkbox checked={checked} onChange={handleOnChange} disabled={localDisabled} />
        )}

        {variant === 'content' && <div className={styles.contentWrap}>{rightContent}</div>}
      </div>
    )
  }
)
